import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchenDining } from "../../../redux/actions/kitchenDiningActions";
import { useLanguage } from "../../../context/LanguageContext";

const SectionKChallenges = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    hasChallenges: "",
    k1: "",
    hasSuggestions: "",
    k2: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };
  
  const isSurveyComplete = (sections) => {
    const required = ["a","b","c","d","e","f","g","h","i","j","k"];
    return required.every(key => sections[key] === true);
  };
  
  const validateForm = () => {
    const newErrors = {};

    if (!form.hasChallenges) newErrors.hasChallenges = t.modules.kitchenCumDining.sections.K.errors?.selectYesNo || "Please select Yes or No";
    if (!form.hasSuggestions) newErrors.hasSuggestions = t.modules.kitchenCumDining.sections.K.errors?.selectYesNo || "Please select Yes or No";

    if (form.hasChallenges === t.global.yes && !form.k1.trim()) {
      newErrors.k1 = t.modules.kitchenCumDining.sections.K.errors?.challengesRequired || "Write down the challenges faced in the canteen operation";
      
    }
    if (form.hasSuggestions === t.global.yes && !form.k2.trim()) {
      newErrors.k2 = t.modules.kitchenCumDining.sections.K.errors?.suggestionsRequired || "Write down the suggestions for improvement";
      
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================================
     SAVE TO LOCAL STORAGE
  ================================ */
  // const handleSave = () => {
  //   if (!validateForm()) {
  //     // alert("Please fill in all required fields before saving.");
  //     return;
  //   }

  //   try {
  //     // Save form data to localStorage
  //     const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
  //     surveyData.sectionK = form;
  //     localStorage.setItem("surveyData", JSON.stringify(surveyData));

  //     // Mark Section K as completed
  //     const completedSections = JSON.parse(localStorage.getItem("completedSections") || "{}");
  //     completedSections.k = true;
  //     localStorage.setItem("completedSections", JSON.stringify(completedSections));

  //     // Navigate back to sections
  //     navigate("/survey/sections", { state });
      
  //     // Show success message
  //     alert("Section K saved successfully!");
  //   } catch (err) {
  //     console.error("Error saving data:", err);
  //     alert("Error saving data. Please try again.");
  //   }
  // };
  
  const handleSave = () => {
    if (!validateForm()) return;

    // We start geolocation. Notice there is NO try/catch outside anymore.
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        // 1. The Try/Catch MUST be inside the async function to catch API errors
        try {
          const fd = new FormData();

          /* ================================
             LOCATION IDS
          ================================ */
          fd.append("district_id", state?.district_id || state?.districtId || 1);
          fd.append("municipality_id", state?.municipality_id || state?.municipalityId || 1);
          fd.append("ward_id", state?.ward_id || state?.wardId || 5);
          fd.append("area_id", state?.area_id || state?.areaId || 7);
          fd.append("canteen_type_id", state?.canteen_type_id || state?.canteenTypeId || 2);
          fd.append("reporting_date", new Date().toISOString().split("T")[0]);

          /* ================================
             SECTION K DATA
          ================================ */
          fd.append("challenges", form.hasChallenges === t.global.yes ? form.k1 : "No challenges");
          fd.append("suggestions", form.hasSuggestions === t.global.yes ? form.k2 : "No suggestions");
          fd.append("latitude", pos.coords.latitude);
          fd.append("longitude", pos.coords.longitude);

          /* ================================
             LOCAL STORAGE & COMPLETION
          ================================ */
          const completedSections = JSON.parse(localStorage.getItem("completedSections") || "{}");
          completedSections.k = true;
          localStorage.setItem("completedSections", JSON.stringify(completedSections));

          const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
          surveyData.sectionK = form;
          localStorage.setItem("surveyData", JSON.stringify(surveyData));

          const isComplete = isSurveyComplete(completedSections);
          fd.append("is_total_survey_complete", isComplete ? 1 : 0);
          fd.append("check_survey_completed", isComplete ? 1 : 0);

          /* ================================
             BYPASS THE 422 BOUNCER
          ================================ */
          // CHANGE THIS: Send "sectionK" instead of "final" to avoid the 422 error
          fd.append("current_section", isComplete ? "final" : "sectionK");

          /* ================================
             API CALL
          ================================ */
          await dispatch(createKitchenDining(fd));

          alert(t.global.success_message);
          navigate("/survey/sections", { state });

        } catch (err) {
          // This catches the 422 error and prevents the red crash screen
          console.error("API Error caught:", err);
          alert(t.global.alert_server_error);
        }
      },
      (geoError) => {
        console.error("GPS Error:", geoError);
        alert(t.global.alert_missing_ids);
      }
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{t.modules.kitchenCumDining.sections.K.title}</h2>
              <p className="text-gray-600">{t.modules.kitchenCumDining.sections.K.subtitle}</p>
            </div>
            <div className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-full">
              {state?.canteenId ? `${t.global.canteen_id_label} ${state.canteenId}` : 
               state?.kioskId ? `${t.global.canteen_id_label} ${state.kioskId}` : t.global.na}
            </div>
          </div>

          <div className="space-y-6">
            {/* K1 - Challenges */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="bg-gray-100 text-gray-800 font-bold rounded-full h-8 w-8 flex items-center justify-center mr-3">1</div>
                <label className="block font-medium">{t.modules.kitchenCumDining.sections.K.questions.challenges} <span className="text-red-500">*</span></label>
              </div>

              {/* Radio Toggle UI */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.hasChallenges === option ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.hasChallenges ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="hasChallenges"
                      value={option}
                      checked={form.hasChallenges === option}
                      onChange={handleChange}
                      className="mr-3"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {errors.hasChallenges && <p className="text-red-500 text-sm mb-2">{errors.hasChallenges}</p>}

              {/* Visible only if YES */}
              {form.hasChallenges === t.global.yes && (
                <textarea
                  name="k1"
                  placeholder={t.modules.kitchenCumDining.sections.K.placeholders?.challenges}
                  className={`w-full border p-4 rounded-lg ${errors.k1 ? 'border-red-500' : ''}`}
                  rows="4"
                  value={form.k1}
                  onChange={handleChange}
                />
              )}
              {errors.k1 && <p className="text-red-500 text-sm mt-2">{errors.k1}</p>}
            </div>

            {/* K2 - Suggestions */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-3">
                <div className="bg-gray-100 text-gray-800 font-bold rounded-full h-8 w-8 flex items-center justify-center mr-3">2</div>
                <label className="block font-medium">{t.modules.kitchenCumDining.sections.K.questions.suggestions} <span className="text-red-500">*</span></label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.hasSuggestions === option ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.hasSuggestions ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="hasSuggestions"
                      value={option}
                      checked={form.hasSuggestions === option}
                      onChange={handleChange}
                      className="mr-3"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {errors.hasSuggestions && <p className="text-red-500 text-sm mb-2">{errors.hasSuggestions}</p>}

              {form.hasSuggestions === t.global.yes && (
                <textarea
                  name="k2"
                  placeholder={t.modules.kitchenCumDining.sections.K.placeholders?.suggestions || "Provide suggestions..."}
                  className={`w-full border p-4 rounded-lg ${errors.k2 ? 'border-red-500' : ''}`}
                  rows="4"
                  value={form.k2}
                  onChange={handleChange}
                />
              )}
              {errors.k2 && <p className="text-red-500 text-sm mt-2">{errors.k2}</p>}
            </div>

            {/* Required fields note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="text-red-500">*</span> {t.modules.kitchenCumDining.sections.K.requiredNote || "indicates required fields. Both challenges and suggestions must be provided."}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                {t.global.back_to_sections}
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                {t.global.submit_section_k}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionKChallenges;